import React, { useRef } from "react";
import styled from "styled-components";
import { 
  Icon, 
  Button
} from "semantic-ui-react";

/**
 * InformationEditor
 *
 * Props:
 *  - blocks: array of objects
 *       e.g. [
 *         { type: "heading", level: "h2", content: "Important" },
 *         { type: "paragraph", content: "More info..." }
 *       ]
 *  - onChange: function(updatedBlocks) => void
 */
export default function InformationEditor({ blocks, onChange }) {
  // -- Block updating helpers --
  const handleBlockChange = (index, updated) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updated };
    onChange(newBlocks);
  };

  const handleAddHeading = () => {
    onChange([...blocks, { type: "heading", level: "h2", content: "New Heading" }]);
  };

  const handleAddParagraph = () => {
    onChange([...blocks, { type: "paragraph", content: "New paragraph..." }]);
  };

  const handleDeleteBlock = (index) => {
    const newBlocks = [...blocks];
    newBlocks.splice(index, 1);
    onChange(newBlocks);
  };

  const moveBlockUp = (index) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    onChange(newBlocks);
  };

  const moveBlockDown = (index) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    onChange(newBlocks);
  };

  const levelOptions = [
    { key: 'h1', text: 'H1', value: 'h1' },
    { key: 'h2', text: 'H2', value: 'h2' },
    { key: 'h3', text: 'H3', value: 'h3' },
    { key: 'h4', text: 'H4', value: 'h4' },
  ];

  return (
    <EditorContainer>
      {blocks.length === 0 ? (
        <EmptyState>
          <EmptyIcon name="file alternate outline" />
          <p>No information blocks yet. Add a heading or paragraph below.</p>
        </EmptyState>
      ) : (
        <BlockList>
          {blocks.map((block, i) => (
            <BlockItem key={i} type={block.type}>
              <BlockItemHeader>
                <BlockType>
                  {block.type === "heading" ? "Heading" : 
                   block.type === "paragraph" ? "Paragraph" : "Unknown Block Type"}
                </BlockType>
                <ActionButtons>
                  <IconButton 
                    disabled={i === 0} 
                    onClick={() => moveBlockUp(i)}
                    aria-label="Move Up"
                  >
                    <Icon name="arrow up" size="small" />
                  </IconButton>
                  <IconButton 
                    disabled={i === blocks.length - 1} 
                    onClick={() => moveBlockDown(i)}
                    aria-label="Move Down"
                  >
                    <Icon name="arrow down" size="small" />
                  </IconButton>
                  <DeleteButton onClick={() => handleDeleteBlock(i)}>
                    <Icon name="trash alternate" size="small" />
                  </DeleteButton>
                </ActionButtons>
              </BlockItemHeader>
              
              {block.type === "heading" && (
                <BlockContent>
                  <HeadingControls>
                    <div>
                      <FieldLabel>Level</FieldLabel>
                      <CustomSelect
                        value={block.level || "h2"}
                        onChange={(e) => handleBlockChange(i, { level: e.target.value })}
                      >
                        {levelOptions.map(option => (
                          <option key={option.key} value={option.value}>
                            {option.text}
                          </option>
                        ))}
                      </CustomSelect>
                    </div>
                    <HeadingTextContainer>
                      <HeadingTextEditor
                        label="Text"
                        value={block.content || ""}
                        onChange={(newVal) => handleBlockChange(i, { content: newVal })}
                      />
                    </HeadingTextContainer>
                  </HeadingControls>
                </BlockContent>
              )}
              
              {block.type === "paragraph" && (
                <BlockContent>
                  <ParagraphTextEditor
                    label="Content"
                    value={block.content || ""}
                    onChange={(newVal) => handleBlockChange(i, { content: newVal })}
                  />
                </BlockContent>
              )}
            </BlockItem>
          ))}
        </BlockList>
      )}
      
      <AddBlockRow>
        <AddButton 
          color="primary" 
          onClick={handleAddHeading}
        >
          <Icon name="heading" />
          Add Heading
        </AddButton>
        <AddButton 
          color="secondary"
          onClick={handleAddParagraph}
        >
          <Icon name="paragraph" />
          Add Paragraph
        </AddButton>
      </AddBlockRow>
    </EditorContainer>
  );
}

/* ----------------------------------------------------------------------------
   Subcomponent: HeadingTextEditor
   Renders a label + input + bracket-tag toolbar for a single-line heading text.
---------------------------------------------------------------------------- */
function HeadingTextEditor({ label, value, onChange }) {
  const inputRef = useRef(null);

  const wrapSelectionInTag = (startTag, endTag) => {
    const input = inputRef.current;
    if (!input) return;

    const { selectionStart, selectionEnd, value: currentVal } = input;

    // If nothing is selected, just insert the start + end tags
    if (selectionStart === selectionEnd) {
      const updated =
        currentVal.slice(0, selectionStart) + startTag + endTag + currentVal.slice(selectionEnd);

      onChange(updated);

      // Move cursor in between the new tags
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(
          selectionStart + startTag.length,
          selectionStart + startTag.length
        );
      });
    } else {
      // Wrap the selection in startTag + endTag
      const selectedText = currentVal.substring(selectionStart, selectionEnd);
      const updated =
        currentVal.slice(0, selectionStart) +
        startTag +
        selectedText +
        endTag +
        currentVal.slice(selectionEnd);

      onChange(updated);

      // Keep selection around newly inserted text
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(
          selectionStart + startTag.length,
          selectionStart + startTag.length + selectedText.length
        );
      });
    }
  };

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <FormattingToolbar>
        <FormatButton onClick={() => wrapSelectionInTag("[b]", "[/b]")} title="Bold">
          B
        </FormatButton>
        <FormatButton onClick={() => wrapSelectionInTag("[hl]", "[/hl]")} title="Highlight">
          HL
        </FormatButton>
      </FormattingToolbar>
      <CustomInput
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter heading text (supports [b][/b] and [hl][/hl])"
      />
    </div>
  );
}

/* ----------------------------------------------------------------------------
   Subcomponent: ParagraphTextEditor
   Renders a label + textarea + bracket-tag toolbar for multi-line paragraph text.
---------------------------------------------------------------------------- */
function ParagraphTextEditor({ label, value, onChange }) {
  const textareaRef = useRef(null);

  const wrapSelectionInTag = (startTag, endTag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value: currentVal } = textarea;

    if (selectionStart === selectionEnd) {
      // No selection, just insert startTag + endTag
      const updated =
        currentVal.slice(0, selectionStart) + startTag + endTag + currentVal.slice(selectionEnd);

      onChange(updated);

      // Move cursor between the tags
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(
          selectionStart + startTag.length,
          selectionStart + startTag.length
        );
      });
    } else {
      // Wrap the selected text
      const selectedText = currentVal.substring(selectionStart, selectionEnd);
      const updated =
        currentVal.slice(0, selectionStart) +
        startTag +
        selectedText +
        endTag +
        currentVal.slice(selectionEnd);

      onChange(updated);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(
          selectionStart + startTag.length,
          selectionStart + startTag.length + selectedText.length
        );
      });
    }
  };

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <FormattingToolbar>
        <FormatButton onClick={() => wrapSelectionInTag("[b]", "[/b]")} title="Bold">
          B
        </FormatButton>
        <FormatButton onClick={() => wrapSelectionInTag("[hl]", "[/hl]")} title="Highlight">
          HL
        </FormatButton>
      </FormattingToolbar>
      <CustomTextarea
        ref={textareaRef}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter paragraph text (supports [b][/b] and [hl][/hl])"
      />
    </div>
  );
}

/* ---------------------------- Styled Components ---------------------------- */

const EditorContainer = styled.div`
  width: 100%;
  background: #ffffff;
`;

const BlockList = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockItem = styled.div`
  border-bottom: 1px solid #eaedf2;
  background: ${props => 
    props.type === "heading" ? "linear-gradient(to right, #ebf5ff 0%, #fbfdff 100%)" : 
    props.type === "paragraph" ? "linear-gradient(to right, #e6fcf5 0%, #fbfffd 100%)" : 
    "#f5f5f5"
  };
`;

const BlockItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
`;

const BlockType = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #2c3e50;
`;

const BlockContent = styled.div`
  padding: 0 16px 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  
  &:hover {
    background: rgba(0, 0, 0, 0.03);
    color: #64748b;
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    
    &:hover {
      background: transparent;
    }
  }
`;

const DeleteButton = styled(IconButton)`
  color: #ef4444;
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
`;

const HeadingControls = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 12px;
  align-items: start;
`;

const HeadingTextContainer = styled.div`
  flex: 1;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #4b5563;
`;

const FormattingToolbar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
`;

const FormatButton = styled.button`
  background: #f3f4f6;
  border: none;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  cursor: pointer;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const CustomInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.1);
  }
`;

const CustomTextarea = styled.textarea`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.1);
  }
`;

const CustomSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.1);
  }
`;

const AddBlockRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #eaedf2;
`;

const AddButton = styled.button`
  background: ${props => props.color === 'primary' ? '#3b82f6' : '#10b981'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.color === 'primary' ? '#2563eb' : '#059669'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px 0;
  color: #9ca3af;
  
  p {
    margin-top: 8px;
    font-size: 14px;
  }
`;

const EmptyIcon = styled(Icon)`
  font-size: 48px !important;
  opacity: 0.4;
`;
